import { Box, Typography } from '@evaneos/b2b-ui';
import PeopleIcon from '@evaneos/b2b-ui/icons/PeopleOutlineOutlined';
import FlightIcon from '@evaneos/b2b-ui/icons/FlightTakeoffOutlined';
import EarthIcon from '@evaneos/b2b-ui/icons/PublicOutlined';
import OutlinedFlag from '@evaneos/b2b-ui/icons/OutlinedFlag';
import { useIntl } from 'react-intl';
import ReactCountryFlag from 'react-country-flag';

import { DisplayProject } from '../../../../resources/dossier-overview/types';
import { DefinitionList } from '../../../../design-system/DefinitionList/DefinitionList';
import { DossierId } from '../../../../resources/dossier/types';
import { useDossierLayoutInfo } from '../../DossierLayoutInfoContext';

import { PaxNumberEditableItem } from './PaxNumberEditableItem';
import { TravelDatesEditableItem } from './TravelDatesEditableItem';

type TravelInfoProps = {
    project: DisplayProject | undefined;
    dossierId: DossierId;
    contractIssued: boolean;
};

const TRAVELLERS_LABEL_ID = 'travellers';
const TRAVEL_DATES_LABEL_ID = 'travel-dates';

export function TravelInfo({ project, dossierId, contractIssued }: Readonly<TravelInfoProps>) {
    const { formatMessage } = useIntl();
    const { isFirstColumnNarrow } = useDossierLayoutInfo();

    return (
        <>
            <DefinitionList.Item alignItems="center">
                <DefinitionList.Label
                    id={TRAVELLERS_LABEL_ID}
                    icon={<PeopleIcon fontSize="small" aria-hidden="true" />}
                    iconOnly={isFirstColumnNarrow}
                >
                    {formatMessage({ id: 'dossier.information.number_of_travellers' })}
                </DefinitionList.Label>
                <PaxNumberEditableItem
                    project={project}
                    dossierId={dossierId}
                    labelId={TRAVELLERS_LABEL_ID}
                    disabled={contractIssued}
                />
            </DefinitionList.Item>
            <DefinitionList.Item>
                <DefinitionList.Label
                    id={TRAVEL_DATES_LABEL_ID}
                    icon={<FlightIcon fontSize="small" aria-hidden="true" />}
                    iconOnly={isFirstColumnNarrow}
                >
                    {formatMessage({ id: 'dossier.information.travel_dates' })}
                </DefinitionList.Label>
                <TravelDatesEditableItem
                    project={project}
                    dossierId={dossierId}
                    labelId={TRAVEL_DATES_LABEL_ID}
                    disabled={contractIssued}
                />
            </DefinitionList.Item>
            <DefinitionList.Item sx={{ alignItems: 'center' }}>
                <DefinitionList.Label
                    id="market"
                    icon={<EarthIcon fontSize="small" aria-hidden="true" />}
                    iconOnly={isFirstColumnNarrow}
                >
                    {formatMessage({ id: 'dossier.project.market' })}
                </DefinitionList.Label>
                <DefinitionList.StaticValue>
                    {project?.marketCode && (
                        <Box
                            component="span"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                '& > img:first-of-type': {
                                    minWidth: '16px',
                                    minHeight: '16px',
                                },
                            }}
                        >
                            <ReactCountryFlag
                                alt={project.marketCode}
                                countryCode={project.marketCode}
                                svg
                            />
                            <Typography component="span" variant="body2">
                                {project.marketCode}
                            </Typography>
                        </Box>
                    )}
                </DefinitionList.StaticValue>
            </DefinitionList.Item>
            <DefinitionList.Item sx={{ alignItems: 'center' }}>
                <DefinitionList.Label
                    id="destination"
                    icon={<OutlinedFlag fontSize="small" aria-hidden="true" />}
                    iconOnly={isFirstColumnNarrow}
                >
                    {formatMessage({ id: 'dossier.project.destination' })}
                </DefinitionList.Label>
                <DefinitionList.StaticValue>
                    {project?.destinationLabel && (
                        <Typography variant="body2">{project.destinationLabel}</Typography>
                    )}
                </DefinitionList.StaticValue>
            </DefinitionList.Item>
        </>
    );
}
